import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import LegalAliases from "@/components/LegalAliases";
import { ArrowLeft, Crown, Calendar, CreditCard, AlertTriangle, Receipt, Loader2 } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso.slice(0, 10); }
}

export default function Account() {
  const { user, refresh } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payments/transactions");
        setTransactions(data.transactions || []);
      } catch { /* noop */ }
      setLoading(false);
    })();
  }, []);

  const cancel = async () => {
    setCancelling(true);
    try {
      await api.post("/payments/cancel");
      toast.success("Auto-renewal cancelled. Your access continues until the period ends.");
      setConfirmCancel(false);
      await refresh?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setCancelling(false); }
  };

  if (!user) return null;

  const isActive = user.membership_status === "active";
  const plan = user.membership_plan;
  const planLabel = plan === "founding" ? "Founding Producer" : plan === "roadmap" ? "Roadmap" : "Free";
  const planPrice = plan === "founding" ? 14 : plan === "roadmap" ? 20 : 0;

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />

      <main className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-white mb-6">
          <ArrowLeft size={12} /> Back to dashboard
        </Link>

        <div className="text-overline mb-3">Account</div>
        <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight mb-3" data-testid="account-title">
          Subscription &amp; Billing
        </h1>
        <p className="text-sm text-neutral-400 mb-12 max-w-2xl">Manage your BeatCredsi™ membership, see renewal date, and review every payment.</p>

        {/* LEGAL ALIASES — surfaced above billing so users can wire it up
            on day one for the Proactive Audit Engine. */}
        <div className="mb-10">
          <LegalAliases />
        </div>

        {/* CURRENT PLAN */}
        <div data-testid="account-current-plan" className="border border-white/5 bg-[#0c0c0c] p-8 mb-px">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                {plan === "founding" && <Crown size={14} className="text-amber-500" />}
                <div className="text-overline">Current Plan</div>
              </div>
              <h2 className="font-display text-3xl font-medium mb-1" data-testid="account-plan-label">{planLabel}</h2>
              <div className="font-mono-tech text-[13px] uppercase tracking-widest">
                {isActive ? (
                  <span className="text-emerald-400">● Active</span>
                ) : (
                  <span className="text-neutral-500">○ Free tier</span>
                )}
              </div>
            </div>
            {planPrice > 0 && (
              <div className="text-right shrink-0">
                <div className="font-display text-4xl font-medium text-amber-500">${planPrice}</div>
                <div className="text-xs uppercase tracking-widest text-neutral-500 font-mono-tech">/ month</div>
              </div>
            )}
          </div>

          {isActive && (
            <div className="mt-6 pt-6 border-t border-white/5 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <Calendar size={14} className="text-neutral-400 mt-1" />
                <div>
                  <div className="text-xs uppercase tracking-widest text-neutral-500 font-mono-tech mb-1">Renews / Expires</div>
                  <div className="text-sm text-white" data-testid="account-renew-date">{fmtDate(user.membership_expires_at)}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CreditCard size={14} className="text-neutral-400 mt-1" />
                <div>
                  <div className="text-xs uppercase tracking-widest text-neutral-500 font-mono-tech mb-1">Billing</div>
                  <div className="text-sm text-white">Stripe · Card on file</div>
                </div>
              </div>
            </div>
          )}

          {!isActive && (
            <div className="mt-6">
              <Link to="/dashboard"
                data-testid="account-upgrade-link"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-amber-500 text-black hover:brightness-110 text-sm font-semibold">
                Upgrade Membership →
              </Link>
            </div>
          )}
        </div>

        {/* CANCEL */}
        {isActive && (
          <div data-testid="account-cancel-card" className="border border-white/5 border-t-0 bg-[#0c0c0c] p-8 mb-12">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="max-w-xl">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle size={14} className="text-rose-400" />
                  <div className="text-overline">Cancel Subscription</div>
                </div>
                <p className="text-sm text-neutral-400">
                  Cancelling stops auto-renewal. Your BeatCredsi™ benefits stay active until <span className="text-white">{fmtDate(user.membership_expires_at)}</span>.
                </p>
              </div>
              {!confirmCancel ? (
                <button onClick={() => setConfirmCancel(true)}
                  data-testid="account-cancel-btn"
                  className="px-4 py-2.5 border border-rose-400/40 text-rose-400 hover:bg-rose-500/10 text-sm font-mono-tech uppercase tracking-widest">
                  Cancel
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <button onClick={cancel} disabled={cancelling}
                    data-testid="account-cancel-confirm"
                    className="px-4 py-2.5 bg-rose-500 text-black hover:brightness-110 text-sm font-semibold inline-flex items-center gap-2">
                    {cancelling && <Loader2 size={12} className="animate-spin" />}
                    Confirm Cancel
                  </button>
                  <button onClick={() => setConfirmCancel(false)} disabled={cancelling}
                    className="px-4 py-2.5 border border-white/20 text-neutral-300 text-sm hover:border-white/40">
                    Keep
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* PAYMENT HISTORY */}
        <div data-testid="account-history" className="border border-white/5 bg-[#0c0c0c] p-8">
          <div className="flex items-center gap-2 mb-5">
            <Receipt size={14} className="text-amber-500" />
            <div className="text-overline">Payment History</div>
          </div>
          {loading ? (
            <div className="text-sm text-neutral-500 font-mono-tech">Loading…</div>
          ) : transactions.length === 0 ? (
            <div className="text-sm text-neutral-500" data-testid="account-history-empty">No transactions yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-widest text-neutral-500 font-mono-tech border-b border-white/5">
                    <th className="py-3 pr-4">Date</th>
                    <th className="py-3 pr-4">Plan</th>
                    <th className="py-3 pr-4 text-right">Amount</th>
                    <th className="py-3 text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((t) => (
                    <tr key={t.session_id} data-testid={`account-tx-${t.session_id}`} className="border-b border-white/5 last:border-0">
                      <td className="py-3 pr-4 text-neutral-300">{fmtDate(t.created_at)}</td>
                      <td className="py-3 pr-4 text-white">{t.package_name || (t.package_id === "founding" ? "Founding" : "Roadmap")}</td>
                      <td className="py-3 pr-4 text-right text-amber-500">${(t.amount || 0).toFixed(2)}</td>
                      <td className="py-3 text-right">
                        <span className={`font-mono-tech text-xs uppercase tracking-widest ${t.payment_status === "paid" ? "text-emerald-400" : t.payment_status === "failed" ? "text-rose-400" : "text-neutral-400"}`}>
                          {t.payment_status || t.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
